import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import QRCodeStyling from 'qr-code-styling';
import {
  FiArrowRight,
  FiCalendar,
  FiClock,
  FiCopy,
  FiDollarSign,
  FiGlobe,
  FiHash,
  FiInfo,
  FiMapPin,
  FiShare2,
  FiShoppingBag,
  FiStar,
  FiTag,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import TopBar from '../components/TopBar';
import ArtistHeroCard from '../components/ArtistHeroCard';
import CollectiblePreviewSurface from '../components/CollectiblePreviewSurface';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import UnavailableState from '../components/UnavailableState';
import { UserContext } from '../App';
import { db, functions } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './ProductDetail.module.css';
import {
  formatCountdown,
  getViewerTimeZone,
  formatDropDateTime,
  formatPrice,
  formatProductSaleStatus,
  getProductCountdown,
  getProductInventory,
  getProductSaleStatus,
} from '../utils/marketplace';
import { timezoneLabel } from '../utils/currency';
import { ANONYMOUS_PHOTO_URL, resolvePublicProfile } from '../utils/profileIdentity';
import {
  isVideoCollectibleMedia,
  useCollectibleMediaUrls,
} from '../utils/collectibleMedia';
import { getArtistSignaturePath, getPrimaryImagePath, getUserPhotoPath, useStorageUrl } from '../utils/storageMedia';
import {
  applyMetadata,
  buildProductMetadata,
  ensureCanonicalUrl,
  getDefaultRouteMetadata,
} from '../seo/metadata';
import { buildAuthNavigationTarget, buildReturnToPath } from '../auth/returnTo';

const DESCRIPTION_COLLAPSE_LIMIT = 200;
const OWNER_PAGE_SIZE = 10;

function OwnerAvatar({ owner }) {
  const photoPath = getUserPhotoPath(owner);
  return (
    <div className={styles.ownerAvatar}>
      {photoPath ? (
        <ResolvedStorageImage
          path={photoPath}
          alt={owner.name}
          className={styles.ownerAvatarImage}
          fallback={<img src={owner.photoUrl || ANONYMOUS_PHOTO_URL} alt={owner.name} className={styles.ownerAvatarImage} />}
        />
      ) : <img src={owner.photoUrl || ANONYMOUS_PHOTO_URL} alt={owner.name} className={styles.ownerAvatarImage} />}
    </div>
  );
}

function buildOwnerRows(units = [], profiles = [], viewerUid = '') {
  const profileMap = new Map(
    profiles.map((profile) => [String(profile?.uid || profile?.id || '').trim(), resolvePublicProfile(profile, viewerUid)])
  );

  return units.map((unit) => {
    const ownerUid = String(unit.ownerUid || '').trim();
    const resolved = profileMap.get(ownerUid) || resolvePublicProfile({ uid: ownerUid, profileVisibility: 'private' }, viewerUid);
    return {
      id: unit.id,
      ownerUid,
      unitNumber: unit.unitNumber || null,
      purchasedAt: unit.purchasedAt || unit.createdAt || null,
      activeListingId: unit.activeListingId || null,
      ...resolved,
    };
  });
}

function formatOwnerDate(ts) {
  if (!ts) return 'Recently added';
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return 'Recently added';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

async function copyText(value = '') {
  if (!value || typeof navigator === 'undefined' || !navigator.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function DropShareQrCard({ url, title, onCopied }) {
  const qrRef = useRef(null);
  const qrHostRef = useRef(null);
  const expandedQrRef = useRef(null);
  const expandedQrHostRef = useRef(null);
  const [qrExpanded, setQrExpanded] = useState(false);

  useEffect(() => {
    const qrHost = qrHostRef.current;
    if (typeof window === 'undefined' || !qrHost || !url) return undefined;
    const qrOptions = {
      width: 168,
      height: 168,
      data: url,
      margin: 0,
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
      dotsOptions: {
        color: '#0f172a',
        type: 'rounded',
      },
      cornersSquareOptions: {
        color: '#0369a1',
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        color: '#f97316',
        type: 'dot',
      },
      backgroundOptions: {
        color: '#f8fafc',
      },
    };

    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling(qrOptions);
    } else {
      qrRef.current.update(qrOptions);
    }

    qrHost.innerHTML = '';
    qrRef.current.append(qrHost);
    return () => {
      qrHost.innerHTML = '';
    };
  }, [url]);

  useEffect(() => {
    const qrHost = expandedQrHostRef.current;
    if (typeof window === 'undefined' || !qrExpanded || !qrHost || !url) return undefined;
    const qrOptions = {
      width: 1000,
      height: 1000,
      data: url,
      margin: 0,
      qrOptions: {
        errorCorrectionLevel: 'Q',
      },
      dotsOptions: {
        color: '#0f172a',
        type: 'rounded',
      },
      cornersSquareOptions: {
        color: '#0369a1',
        type: 'extra-rounded',
      },
      cornersDotOptions: {
        color: '#f97316',
        type: 'dot',
      },
      backgroundOptions: {
        color: '#f8fafc',
      },
    };

    if (!expandedQrRef.current) {
      expandedQrRef.current = new QRCodeStyling(qrOptions);
    } else {
      expandedQrRef.current.update(qrOptions);
    }

    qrHost.innerHTML = '';
    expandedQrRef.current.append(qrHost);
    return () => {
      qrHost.innerHTML = '';
    };
  }, [qrExpanded, url]);

  useEffect(() => {
    if (!qrExpanded || typeof document === 'undefined') return undefined;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setQrExpanded(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [qrExpanded]);

  const handleCopy = useCallback(async () => {
    const copied = await copyText(url);
    if (copied) onCopied?.();
  }, [onCopied, url]);

  const handleShare = useCallback(async () => {
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({
          title: title || 'Collectible Release',
          url,
        });
        return;
      } catch (error) {
        if (error?.name === 'AbortError') return;
      }
    }
    await handleCopy();
  }, [handleCopy, title, url]);

  return (
    <section className={`${styles.block} ${styles.dropQrCard}`.trim()}>
      <div className={styles.dropQrGrid}>
        <div className={styles.dropQrCopy}>
          <span className={styles.dropQrEyebrow}>Editorial QR</span>
          <button type="button" className={styles.dropQrShareButton} onClick={handleShare}>
            <FiShare2 />
            Share
          </button>
          <button type="button" className={styles.dropQrUrlButton} onClick={handleCopy}>
            <span className={styles.dropQrUrl}>{url}</span>
            <FiCopy />
          </button>
        </div>
        <button
          type="button"
          className={styles.dropQrFrame}
          onClick={() => setQrExpanded(true)}
          aria-label="Open large QR code"
        >
          <div className={styles.dropQrCanvas} ref={qrHostRef} aria-hidden="true" />
        </button>
      </div>
      {qrExpanded ? (
        <div className={styles.dropQrModal} role="dialog" aria-modal="true" aria-label="Large QR code" onClick={() => setQrExpanded(false)}>
          <div className={styles.dropQrModalCard} onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className={styles.dropQrModalClose}
              onClick={() => setQrExpanded(false)}
              aria-label="Close QR code"
            >
              <FiX />
            </button>
            <div className={styles.dropQrModalCanvas} ref={expandedQrHostRef} aria-hidden="true" />
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default function ProductDetail() {
  const { productId } = useParams();
  const appUser = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [mediaError, setMediaError] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [owners, setOwners] = useState([]);
  const [ownersLoading, setOwnersLoading] = useState(true);
  const [ownersLoadingMore, setOwnersLoadingMore] = useState(false);
  const [ownersError, setOwnersError] = useState('');
  const [ownersCursor, setOwnersCursor] = useState(null);
  const [ownersHasMore, setOwnersHasMore] = useState(false);
  const [copyToastVisible, setCopyToastVisible] = useState(false);
  const copyToastTimerRef = useRef(null);
  const isSignedIn = Boolean(appUser?.id);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setDescriptionExpanded(false);
    setLoadError('');
    setMediaError('');
  }, [productId]);

  useEffect(() => () => {
    if (copyToastTimerRef.current) {
      window.clearTimeout(copyToastTimerRef.current);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      const productSnap = await getDoc(doc(db, 'products', String(productId)));
      if (!productSnap.exists()) {
        setProduct(null);
        setLoadError('Product not found.');
        return;
      }
      const productData = { id: productSnap.id, ...(productSnap.data() || {}) };
      const artistSnap = productData.artistId ? await getDoc(doc(db, 'artists', String(productData.artistId))) : null;
      setProduct(productData);
      setArtist(artistSnap && artistSnap.exists() ? { id: artistSnap.id, ...(artistSnap.data() || {}) } : null);
    } catch (err) {
      setProduct(null);
      setArtist(null);
      setLoadError(err?.message || 'Unable to load product.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  const loadOwners = useCallback(async ({ reset = false, afterDoc = null } = {}) => {
    const setBusy = reset ? setOwnersLoading : setOwnersLoadingMore;
    setBusy(true);
    if (reset) {
      setOwners([]);
      setOwnersCursor(null);
      setOwnersHasMore(false);
      setOwnersError('');
    }

    try {
      const ownerQuery = query(
        collection(db, 'units'),
        where('productId', '==', String(productId || '')),
        orderBy('unitNumber', 'asc'),
        ...(afterDoc ? [startAfter(afterDoc), limit(OWNER_PAGE_SIZE)] : [limit(OWNER_PAGE_SIZE)])
      );
      const unitsSnap = await getDocs(ownerQuery);
      const unitRows = unitsSnap.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));
      const ownerUids = [...new Set(unitRows.map((row) => String(row.ownerUid || '').trim()).filter(Boolean))];
      let profiles = [];

      if (ownerUids.length) {
        const callable = httpsCallable(functions, 'getPublicOwnerProfiles');
        const response = await callable({ uids: ownerUids });
        profiles = Array.isArray(response?.data?.profiles) ? response.data.profiles : [];
      }

      const rows = buildOwnerRows(unitRows, profiles, appUser?.id);
      setOwners((current) => (reset ? rows : [...current, ...rows]));
      setOwnersCursor(unitsSnap.docs[unitsSnap.docs.length - 1] || null);
      setOwnersHasMore(unitsSnap.docs.length === OWNER_PAGE_SIZE);
    } catch (err) {
      setOwnersError(err?.message || 'Unable to load collectors.');
    } finally {
      setBusy(false);
    }
  }, [appUser?.id, productId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (!isSignedIn) {
      setOwners([]);
      setOwnersCursor(null);
      setOwnersHasMore(false);
      setOwnersError('');
      setOwnersLoading(false);
      setOwnersLoadingMore(false);
      return;
    }
    loadOwners({ reset: true });
  }, [isSignedIn, loadOwners]);

  const inventory = useMemo(() => getProductInventory(product || {}), [product]);
  const saleStatus = useMemo(() => getProductSaleStatus(product || {}, nowMs), [product, nowMs]);
  const saleLabel = useMemo(() => formatProductSaleStatus(product || {}, nowMs), [product, nowMs]);
  const countdown = useMemo(() => getProductCountdown(product || {}, nowMs), [product, nowMs]);
  const canBuy = saleStatus === 'active' && inventory.availableCount > 0;
  const { mediaUrl: collectibleMediaUrl, playbackUrl, posterUrl } = useCollectibleMediaUrls(product || {});
  const primaryImageUrl = useStorageUrl(getPrimaryImagePath(product || {}));
  const artistSignatureUrl = useStorageUrl(getArtistSignaturePath(artist || {}));
  const mediaUrl = collectibleMediaUrl || primaryImageUrl;
  const hasPlayableCollectibleVideo = isVideoCollectibleMedia(product || {}) && Boolean(playbackUrl);
  const backTo = String(location.state?.backTo || '');
  const fullDescription = (product?.description || 'No description added yet.').trim();
  const needsDescriptionToggle = fullDescription.length > DESCRIPTION_COLLAPSE_LIMIT;
  const visibleDescription = descriptionExpanded || !needsDescriptionToggle
    ? fullDescription
    : `${fullDescription.slice(0, DESCRIPTION_COLLAPSE_LIMIT).trimEnd()}…`;
  const isDropProduct = Boolean(product?.dropId);
  const returnTo = buildReturnToPath(location);
  const guestBuyPath = buildAuthNavigationTarget('/signin', returnTo);
  const viewerTimezone = getViewerTimeZone();
  const productMetadata = useMemo(() => (
    product
      ? buildProductMetadata({ product, productId, artist })
      : {
        ...getDefaultRouteMetadata(`/products/${productId}`),
        pathname: `/products/${productId}`,
        canonical: ensureCanonicalUrl(`/products/${productId}`),
      }
  ), [artist, product, productId]);
  const productCanonicalUrl = productMetadata?.canonical || ensureCanonicalUrl(`/products/${productId}`);

  const showCopyToast = useCallback(() => {
    setCopyToastVisible(true);
    if (copyToastTimerRef.current) window.clearTimeout(copyToastTimerRef.current);
    copyToastTimerRef.current = window.setTimeout(() => {
      setCopyToastVisible(false);
      copyToastTimerRef.current = null;
    }, 2000);
  }, []);

  useEffect(() => {
    if (!productId) return;
    applyMetadata(productMetadata);
  }, [productId, productMetadata]);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar
        variant="back"
        backLabel="Back"
        onBack={() => (backTo ? navigate(backTo) : navigate(-1))}
        rightActionLabel="Home"
        onRightAction={() => navigate(isSignedIn ? '/home' : '/')}
      />
      <div className={`${layoutStyles.detailMain} ${styles.page}`.trim()}>
        {copyToastVisible ? <div className={styles.copyToast} role="status" aria-live="polite">Copied</div> : null}
        {loading ? <p className={styles.state}>Loading product…</p> : null}
        {!loading && loadError && !product ? (
          <UnavailableState
            eyebrow="Product Unavailable"
            title="This product is unavailable."
            message={loadError}
            onPrimary={() => navigate('/home')}
            primaryLabel="Browse Home"
            onSecondary={() => navigate(-1)}
            secondaryLabel="Go Back"
          />
        ) : null}
        {!loading && product ? (
          <div className={styles.pageStack}>
            <div className={styles.shell}>
              <div className={styles.mediaColumn}>
                <CollectiblePreviewSurface
                  hasPlayableVideo={hasPlayableCollectibleVideo}
                  mediaUrl={mediaUrl}
                  playbackUrl={playbackUrl}
                  posterUrl={posterUrl || mediaUrl}
                  backTextureUrl={artistSignatureUrl}
                  title={product.title || 'Product'}
                  placeholderLabel="Product"
                  onPlaybackError={(err) => setMediaError(err?.message || 'Unable to play video.')}
                />
                {mediaError ? <p className={styles.error}>{mediaError}</p> : null}
                {countdown ? (
                  <div className={`${styles.heroCountdown} ${countdown.status === 'active' ? styles.heroCountdownLive : ''}`.trim()}>
                    <span>{countdown.label}</span>
                    <strong>{formatCountdown(countdown.targetMs, nowMs)}</strong>
                  </div>
                ) : null}
              </div>
              <div className={styles.infoPane}>
                <div className={styles.topline}>
                  <span className={styles.eyebrow}>Collectible release</span>
                  <span className={saleStatus === 'active' ? styles.badgeLive : styles.badgeState}>{saleLabel}</span>
                </div>
                <ArtistHeroCard
                  artist={artist}
                  eyebrow="Artist"
                  subtitle="Open artist page"
                  onClick={artist?.id ? () => navigate(`/artists/${artist.id}`) : null}
                />
                <div className={styles.titleWrap}>
                  <h1>{product.title || product.id}</h1>
                  <p className={styles.price}><FiDollarSign /> {formatPrice(product)}</p>
                </div>
                <div className={styles.summaryBlock}>
                  <p className={styles.summary}>{visibleDescription}</p>
                  {needsDescriptionToggle ? (
                    <button
                      type="button"
                      className={styles.readMoreButton}
                      onClick={() => setDescriptionExpanded((prev) => !prev)}
                    >
                      {descriptionExpanded ? 'Read less' : 'Read more'}
                    </button>
                  ) : null}
                </div>
                <section className={styles.block}>
                  <div className={styles.sectionTitle}>
                    <div>
                      <h2><FiShoppingBag /> Primary Sale</h2>
                    </div>
                  </div>
                  {canBuy ? (
                    isSignedIn ? (
                      <button type="button" className={styles.buyButton} onClick={() => navigate(`/products/${productId}/preview`)}>
                        <FiShoppingBag />
                        Buy Now
                      </button>
                    ) : (
                      <button type="button" className={styles.buyButton} onClick={() => navigate(guestBuyPath, { state: { returnTo } })}>
                        <FiShoppingBag />
                        Sign in to buy
                      </button>
                    )
                  ) : (
                    <p className={styles.state}>
                      {saleStatus === 'blocked'
                        ? 'This primary sale is temporarily unavailable right now.'
                        : `This primary sale is currently ${saleLabel.toLowerCase()}.`}
                    </p>
                  )}
                </section>

                <div className={styles.metaGrid}>
                  {saleStatus !== 'ended' ? <div><span><FiHash /> Remaining</span><strong>{inventory.availableCount}</strong></div> : null}
                  {!isDropProduct ? <div><span><FiStar /> Sold</span><strong>{inventory.soldCount}</strong></div> : null}
                  {!isDropProduct ? <div><span><FiTag /> Max Supply</span><strong>{inventory.maxSupply}</strong></div> : null}
                  {product.cityName ? <div><span><FiMapPin /> City</span><strong>{product.cityName}</strong></div> : null}
                  <div><span><FiGlobe /> Your Timezone</span><strong>{timezoneLabel(viewerTimezone)}</strong></div>
                  <div><span><FiDollarSign /> Currency</span><strong>{product.currency || 'USD'}</strong></div>
                  {product.durationHours ? <div><span><FiClock /> Duration</span><strong>{product.durationHours} hour{Number(product.durationHours) === 1 ? '' : 's'}</strong></div> : null}
                  {isDropProduct ? <div><span><FiInfo /> Release Model</span><strong>Drop</strong></div> : null}
                </div>

                {isDropProduct ? <DropShareQrCard url={productCanonicalUrl} title={product?.title || product?.id} onCopied={showCopyToast} /> : null}
                <section className={styles.block}>
                  <div className={styles.sectionTitle}>
                    <div>
                      <h2><FiCalendar /> Sale Window</h2>
                      <p>Track the live availability of this drop.</p>
                    </div>
                  </div>
                  <div className={styles.metaGrid}>
                    <div><span><FiArrowRight /> Starts</span><strong>{formatDropDateTime(product, 'saleStartAt')}</strong></div>
                    <div><span><FiArrowRight /> Ends</span><strong>{formatDropDateTime(product, 'saleEndAt')}</strong></div>
                  </div>
                </section>
              </div>
            </div>

            <section className={`${styles.block} ${styles.ownersSection}`.trim()}>
              <div className={styles.sectionTitle}>
                <div>
                  <h2><FiUsers /> Collectors</h2>
                  <p>{isSignedIn ? 'See who currently owns each sold unit, starting with Unit #1.' : 'Collector ownership details stay private until you sign in.'}</p>
                </div>
              </div>
              {isSignedIn && ownersLoading ? <p className={styles.state}>Loading collectors…</p> : null}
              {isSignedIn && ownersError ? <p className={styles.error}>{ownersError}</p> : null}
              {isSignedIn && !ownersLoading && !ownersError && owners.length ? (
                <>
                  <div className={styles.ownerList}>
                    {owners.map((owner) => (
                      <div key={owner.id} className={styles.ownerRow}>
                        <div className={styles.ownerIdentity}>
                          <OwnerAvatar owner={owner} />
                          <div className={styles.ownerCopy}>
                            <strong>{owner.name}</strong>
                            <p>{owner.isAnonymous ? 'Private collector' : 'Current owner'}</p>
                          </div>
                        </div>
                        <div className={styles.ownerMeta}>
                          <span><FiHash /> Unit #{owner.unitNumber || '—'}</span>
                          <span><FiCalendar /> {formatOwnerDate(owner.purchasedAt)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {ownersHasMore ? (
                    <div className={styles.loadMoreRow}>
                      <button type="button" className={styles.loadMoreButton} onClick={() => loadOwners({ afterDoc: ownersCursor })} disabled={ownersLoadingMore}>
                        {ownersLoadingMore ? 'Loading…' : 'Load 10 More'}
                      </button>
                    </div>
                  ) : null}
                </>
              ) : null}
              {!isSignedIn ? (
                <div className={styles.emptyOwners}>
                  <strong>Collector details unlock after sign-in.</strong>
                  <p>You can browse the collectible, artist, price, and release timing publicly. Ownership and collector identity stay private.</p>
                </div>
              ) : null}
              {isSignedIn && !ownersLoading && !ownersError && !owners.length ? (
                <div className={styles.emptyOwners}>
                  <strong>No collectors yet.</strong>
                  <p>This release has not recorded any sold units yet. Once purchases start, owners will appear here in unit order.</p>
                </div>
              ) : null}
            </section>
          </div>
        ) : null}
      </div>
    </div>
  );
}
